"use client";

import { useEffect, useMemo, useState } from "react";
import { Megaphone } from "lucide-react";
import { Button } from "./button";
import { useTranslations } from "@/components/i18n/use-translations";
import { BETA_NOTICE_STORAGE_KEY } from "./onboarding-storage";
import { ModalOverlay } from "./modal-overlay";
import { OnboardingNoticeCard } from "./onboarding-notice-card";

type BetaNoticeProps = {
    onDismiss?: () => void;
};

const primaryButtonClassName =
    "bg-linear-to-r from-cyan-500 to-blue-600 text-white font-semibold shadow-lg shadow-cyan-500/30 hover:from-cyan-400 hover:to-blue-500 hover:text-white rounded-full";

export function BetaNotice({ onDismiss }: BetaNoticeProps) {
    const [isOpen, setIsOpen] = useState(false);
    const { messages } = useTranslations();
    const titleLines = useMemo(() => messages.betaNotice.title.split("\n"), [messages.betaNotice.title]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const hasSeenNotice = window.localStorage.getItem(BETA_NOTICE_STORAGE_KEY);
        if (!hasSeenNotice) {
            setIsOpen(true);
        }
    }, []);

    const handleDismiss = () => {
        if (typeof window !== "undefined") {
            window.localStorage.setItem(BETA_NOTICE_STORAGE_KEY, "acknowledged");
        }
        setIsOpen(false);
        onDismiss?.();
    };

    if (!isOpen) {
        return null;
    }

    return (
        <ModalOverlay>
            <OnboardingNoticeCard
                badge={messages.betaNotice.badge}
                titleLines={titleLines}
                description={messages.betaNotice.description}
                bullets={[...messages.betaNotice.bullets]}
                icon={<Megaphone className="h-5 w-5" strokeWidth={1.75} />}
                secondaryLabel={messages.betaNotice.maybeLater}
                dismissLabel={messages.betaNotice.dismissLabel}
                onDismiss={handleDismiss}
                primaryAction={
                    <Button className={primaryButtonClassName} onClick={handleDismiss}>
                        {messages.betaNotice.startExploring}
                    </Button>
                }
            />
        </ModalOverlay>
    );
}
